"use strict";
// Copyright (C) 2017-2026 Smart code 203358507
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var react_i18next_1 = require("react-i18next");
var common_1 = require("stremio/common");
var subtitleDelay_1 = require("./subtitleDelay");
var withFallbackLabels = function (tracks) {
    if (!Array.isArray(tracks)) {
        return [];
    }
    return tracks.map(function (track) { return (__assign(__assign({}, track), { label: track.label || track.url || '' })); });
};
var findTrackById = function (tracks, id) {
    if (!id) {
        return undefined;
    }
    return tracks.find(function (track) { return track.id === id; });
};
var normalizeLanguage = function (language) {
    var _a;
    if (!language) {
        return undefined;
    }
    var value = language.trim();
    var normalized = (_a = common_1.languages.find(value)) !== null && _a !== void 0 ? _a : common_1.languages.find(value.toLowerCase());
    return normalized === null || normalized === void 0 ? void 0 : normalized.code;
};
var findTrackByLanguage = function (tracks, language) {
    var languageCode = normalizeLanguage(language);
    if (!languageCode) {
        return undefined;
    }
    return tracks.find(function (track) { return normalizeLanguage(track.lang) === languageCode; });
};
var candidateMatchesTrack = function (candidate, source, track) {
    if (candidate.source !== source || (candidate.id && candidate.id !== track.id)) {
        return false;
    }
    return !candidate.language || normalizeLanguage(track.lang) === candidate.language;
};
var resolveCandidate = function (candidate, subtitlesTracks, extraSubtitlesTracks) {
    var tracks = candidate.source === 'embedded' ? subtitlesTracks : extraSubtitlesTracks;
    var track = candidate.id ?
        findTrackById(tracks, candidate.id)
        :
            findTrackByLanguage(tracks, candidate.language);
    return track && (!candidate.language || normalizeLanguage(track.lang) === candidate.language) ?
        track
        :
            undefined;
};
var buildCandidates = function (sessionPreference, savedTrack, globalLanguage) {
    var candidates = [];
    var languagesOrder = [];
    var sessionEnabled = (sessionPreference === null || sessionPreference === void 0 ? void 0 : sessionPreference.enabled) === true;
    var sessionLanguage = normalizeLanguage(sessionPreference === null || sessionPreference === void 0 ? void 0 : sessionPreference.language);
    var savedLanguage = normalizeLanguage(savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.language);
    var savedSource = savedTrack ? (savedTrack.embedded ? 'embedded' : 'external') : undefined;
    var preferredSource = sessionEnabled ? sessionPreference.source : savedSource;
    var sources = preferredSource === 'external' ?
        ['external', 'embedded']
        :
            ['embedded', 'external'];
    var addLanguage = function (language) {
        if (language && !languagesOrder.includes(language)) {
            languagesOrder.push(language);
        }
    };
    if ((savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.id) && (!sessionEnabled ||
        !sessionPreference.source || sessionPreference.source === savedSource)) {
        candidates.push(__assign({ source: savedSource, id: savedTrack.id }, (sessionLanguage ? { language: sessionLanguage } : {})));
    }
    if (sessionEnabled) {
        addLanguage(sessionLanguage !== null && sessionLanguage !== void 0 ? sessionLanguage : savedLanguage);
    }
    else {
        addLanguage(savedLanguage);
    }
    addLanguage(normalizeLanguage(globalLanguage));
    if (sessionEnabled) {
        addLanguage(normalizeLanguage(common_1.CONSTANTS.DEFAULT_SUBTITLES_LANGUAGE));
    }
    // Keep language ahead of source so the selected language can cross source types.
    languagesOrder.forEach(function (language) {
        sources.forEach(function (source) { return candidates.push({ source: source, language: language }); });
    });
    return candidates;
};
var resolveBestCandidate = function (candidates, subtitlesTracks, extraSubtitlesTracks) {
    for (var rank = 0; rank < candidates.length; rank++) {
        var candidate = candidates[rank];
        var track = resolveCandidate(candidate, subtitlesTracks, extraSubtitlesTracks);
        if (track) {
            return { source: candidate.source, rank: rank, track: track };
        }
    }
    return undefined;
};
var findCandidateRank = function (candidates, source, track) {
    return candidates.findIndex(function (candidate) { return candidateMatchesTrack(candidate, source, track); });
};
var useSubtitles = function (_a) {
    var player = _a.player, video = _a.video, settings = _a.settings, streamStateChanged = _a.streamStateChanged, subtitlePreferenceChanged = _a.subtitlePreferenceChanged, menusOpen = _a.menusOpen, closeMenus = _a.closeMenus, closeSubtitlesMenu = _a.closeSubtitlesMenu, toggleSubtitlesMenu = _a.toggleSubtitlesMenu;
    var t = (0, react_i18next_1.useTranslation)().t;
    var setSubtitlesTrack = video.setSubtitlesTrack, setExtraSubtitlesTrack = video.setExtraSubtitlesTrack, setSubtitlesDelay = video.setSubtitlesDelay, setSubtitlesSize = video.setSubtitlesSize, setSubtitlesOffset = video.setSubtitlesOffset;
    var toast = (0, common_1.useToast)();
    var videoRef = (0, react_1.useRef)(video);
    var settingsRef = (0, react_1.useRef)(settings);
    var trackSelectionLocked = (0, react_1.useRef)(false);
    var appliedTrack = (0, react_1.useRef)(null);
    var lastSelectedTrack = (0, react_1.useRef)(null);
    var subtitleDelayHold = (0, react_1.useRef)(null);
    var subtitleDelayInterval = (0, common_1.useInterval)(subtitleDelay_1.SUBTITLES_DELAY_REPEAT_INTERVAL_MS);
    var subtitleDelayTimeout = (0, common_1.useTimeout)(subtitleDelay_1.SUBTITLES_DELAY_REPEAT_DELAY_MS);
    videoRef.current = video;
    settingsRef.current = settings;
    var streamSubtitles = (0, react_1.useMemo)(function () {
        var _a;
        return withFallbackLabels((_a = player.selected) === null || _a === void 0 ? void 0 : _a.stream.subtitles);
    }, [player.selected]);
    var externalSubtitles = (0, react_1.useMemo)(function () {
        return withFallbackLabels(player.subtitles);
    }, [player.subtitles]);
    var allTracks = (0, react_1.useMemo)(function () {
        return video.state.subtitlesTracks.concat(video.state.extraSubtitlesTracks);
    }, [video.state.subtitlesTracks, video.state.extraSubtitlesTracks]);
    var hasTracks = allTracks.length > 0;
    var applySubtitleStyle = (0, react_1.useCallback)(function () {
        var currentSettings = settingsRef.current;
        var currentVideo = videoRef.current;
        currentVideo.setSubtitlesSize(currentSettings.subtitlesSize);
        currentVideo.setSubtitlesOffset(currentSettings.subtitlesOffset);
        currentVideo.setSubtitlesTextColor(currentSettings.subtitlesTextColor);
        currentVideo.setSubtitlesBackgroundColor(currentSettings.subtitlesBackgroundColor);
        currentVideo.setSubtitlesOutlineColor(currentSettings.subtitlesOutlineColor);
    }, []);
    var rememberTrack = (0, react_1.useCallback)(function (track, embedded) {
        var language = normalizeLanguage(track.lang);
        lastSelectedTrack.current = __assign({ id: track.id, embedded: embedded }, (language ? { language: language } : {}));
        streamStateChanged({
            subtitleTrack: {
                id: track.id,
                embedded: embedded,
                language: track.lang,
            },
        });
        subtitlePreferenceChanged(__assign({ enabled: true, source: embedded ? 'embedded' : 'external' }, (language ? { language: language } : {})));
    }, [streamStateChanged, subtitlePreferenceChanged]);
    var disableSubtitles = (0, react_1.useCallback)(function () {
        var _a, _b, _c, _d;
        var selectedTrack = video.state.selectedSubtitlesTrackId !== null ?
            findTrackById(video.state.subtitlesTracks, video.state.selectedSubtitlesTrackId)
            :
                findTrackById(video.state.extraSubtitlesTracks, video.state.selectedExtraSubtitlesTrackId);
        var selectedSource = video.state.selectedSubtitlesTrackId !== null ?
            'embedded'
            :
                video.state.selectedExtraSubtitlesTrackId !== null ?
                    'external'
                    :
                        undefined;
        var source = (_b = (_a = player.subtitlePreference) === null || _a === void 0 ? void 0 : _a.source) !== null && _b !== void 0 ? _b : selectedSource;
        var language = (_d = (_c = player.subtitlePreference) === null || _c === void 0 ? void 0 : _c.language) !== null && _d !== void 0 ? _d : normalizeLanguage(selectedTrack === null || selectedTrack === void 0 ? void 0 : selectedTrack.lang);
        trackSelectionLocked.current = true;
        appliedTrack.current = null;
        setSubtitlesTrack(null);
        setExtraSubtitlesTrack(null);
        streamStateChanged({ subtitleTrack: null });
        subtitlePreferenceChanged(__assign(__assign({ enabled: false }, (source ? { source: source } : {})), (language ? { language: language } : {})));
    }, [
        player.subtitlePreference,
        streamStateChanged,
        subtitlePreferenceChanged,
        setSubtitlesTrack,
        setExtraSubtitlesTrack,
        video.state.subtitlesTracks,
        video.state.extraSubtitlesTracks,
        video.state.selectedSubtitlesTrackId,
        video.state.selectedExtraSubtitlesTrackId,
    ]);
    var selectEmbeddedTrack = (0, react_1.useCallback)(function (track) {
        if (!track) {
            disableSubtitles();
            return;
        }
        trackSelectionLocked.current = true;
        appliedTrack.current = { id: track.id, source: 'embedded' };
        setSubtitlesTrack(track.id);
        rememberTrack(track, true);
    }, [disableSubtitles, rememberTrack, setSubtitlesTrack]);
    var selectExtraTrack = (0, react_1.useCallback)(function (track) {
        if (!track) {
            disableSubtitles();
            return;
        }
        trackSelectionLocked.current = true;
        appliedTrack.current = { id: track.id, source: 'external' };
        setExtraSubtitlesTrack(track.id);
        rememberTrack(track, false);
    }, [disableSubtitles, rememberTrack, setExtraSubtitlesTrack]);
    var changeDelay = (0, react_1.useCallback)(function (delay) {
        setSubtitlesDelay(delay);
        streamStateChanged({ subtitleDelay: delay });
    }, [streamStateChanged, setSubtitlesDelay]);
    var finishSubtitleDelayHold = (0, react_1.useCallback)(function (applyStep) {
        var hold = subtitleDelayHold.current;
        subtitleDelayInterval.cancel();
        subtitleDelayTimeout.cancel();
        subtitleDelayHold.current = null;
        if (applyStep && hold && !hold.repeated) {
            var delay = hold.value + hold.direction * subtitleDelay_1.SUBTITLES_DELAY_STEP_MS;
            changeDelay((0, subtitleDelay_1.snapSubtitleDelay)(delay, hold.direction));
        }
    }, [changeDelay]);
    var startSubtitleDelayHold = (0, react_1.useCallback)(function (combo, key, repeat) {
        var _a;
        if (repeat || subtitleDelayHold.current)
            return;
        var hold = {
            direction: combo === 1 ? 1 : -1,
            key: key.toUpperCase(),
            repeated: false,
            startedAt: performance.now(),
            stream: videoRef.current.state.stream,
            value: (_a = videoRef.current.state.extraSubtitlesDelay) !== null && _a !== void 0 ? _a : 0,
        };
        subtitleDelayHold.current = hold;
        subtitleDelayTimeout.start(function () { return subtitleDelayInterval.start(function () {
            if (subtitleDelayHold.current !== hold)
                return;
            if (videoRef.current.state.stream !== hold.stream) {
                finishSubtitleDelayHold(false);
                return;
            }
            hold.repeated = true;
            var multiplier = (0, subtitleDelay_1.getSubtitleDelayStepMultiplier)(performance.now() - hold.startedAt);
            var delay = hold.value + hold.direction * subtitleDelay_1.SUBTITLES_DELAY_STEP_MS * multiplier;
            hold.value = (0, subtitleDelay_1.snapSubtitleDelay)(delay, hold.direction);
            changeDelay(hold.value);
        }); });
    }, [changeDelay, finishSubtitleDelayHold]);
    (0, react_1.useEffect)(function () {
        var onKeyUp = function (event) {
            var _a;
            var key = (_a = subtitleDelayHold.current) === null || _a === void 0 ? void 0 : _a.key;
            if (!key)
                return;
            var keys = (0, common_1.getKeyboardShortcutKeys)(event);
            if (keys.includes("Key".concat(key)) || keys.includes(key)) {
                finishSubtitleDelayHold(true);
            }
        };
        var onBlur = function () { return finishSubtitleDelayHold(false); };
        document.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return function () {
            document.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, [finishSubtitleDelayHold]);
    (0, react_1.useEffect)(function () {
        if (menusOpen)
            finishSubtitleDelayHold(false);
    }, [finishSubtitleDelayHold, menusOpen]);
    var changeSize = (0, react_1.useCallback)(function (size) {
        setSubtitlesSize(size);
        streamStateChanged({ subtitleSize: size });
    }, [streamStateChanged, setSubtitlesSize]);
    var updateSize = (0, react_1.useCallback)(function (delta) {
        var _a;
        var sizes = common_1.CONSTANTS.SUBTITLES_SIZES;
        var sizeIndex = sizes.indexOf((_a = video.state.subtitlesSize) !== null && _a !== void 0 ? _a : -1);
        var nextIndex = Math.max(0, Math.min(sizes.length - 1, sizeIndex + delta));
        changeSize(sizes[nextIndex]);
    }, [changeSize, video.state.subtitlesSize]);
    var changeOffset = (0, react_1.useCallback)(function (offset) {
        setSubtitlesOffset(offset);
        streamStateChanged({ subtitleOffset: offset });
    }, [streamStateChanged, setSubtitlesOffset]);
    var onSubtitlesDrop = (0, react_1.useCallback)(function (file, buffer) {
        videoRef.current.addLocalSubtitles(file.name, buffer);
    }, []);
    (0, common_1.useFileDropListener)(common_1.CONSTANTS.SUPPORTED_LOCAL_SUBTITLES, onSubtitlesDrop);
    (0, react_1.useEffect)(function () {
        if (video.state.stream !== null) {
            video.addExtraSubtitlesTracks(externalSubtitles);
        }
    }, [externalSubtitles, video.state.stream]);
    (0, react_1.useEffect)(function () {
        trackSelectionLocked.current = false;
        appliedTrack.current = null;
        lastSelectedTrack.current = null;
    }, [video.state.stream]);
    (0, react_1.useEffect)(function () {
        var _a, _b, _c;
        if (trackSelectionLocked.current) {
            return;
        }
        var sessionPreference = player.subtitlePreference;
        var sessionEnabled = (sessionPreference === null || sessionPreference === void 0 ? void 0 : sessionPreference.enabled) === true;
        if ((sessionPreference === null || sessionPreference === void 0 ? void 0 : sessionPreference.enabled) === false || (!sessionEnabled && settings.subtitlesLanguage === null)) {
            if (video.state.selectedSubtitlesTrackId !== null ||
                video.state.selectedExtraSubtitlesTrackId !== null) {
                setSubtitlesTrack(null);
                setExtraSubtitlesTrack(null);
            }
            appliedTrack.current = null;
            return;
        }
        var savedTrack = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleTrack;
        var candidates = buildCandidates(sessionPreference, savedTrack, settings.subtitlesLanguage);
        var bestCandidate = resolveBestCandidate(candidates, video.state.subtitlesTracks, video.state.extraSubtitlesTracks);
        var selectedSource = video.state.selectedSubtitlesTrackId !== null ?
            'embedded'
            :
                video.state.selectedExtraSubtitlesTrackId !== null ?
                    'external'
                    :
                        undefined;
        var selectedTrack = selectedSource === 'embedded' ?
            findTrackById(video.state.subtitlesTracks, video.state.selectedSubtitlesTrackId)
            :
                selectedSource === 'external' ?
                    findTrackById(video.state.extraSubtitlesTracks, video.state.selectedExtraSubtitlesTrackId)
                    :
                        undefined;
        if (!bestCandidate) {
            if (sessionEnabled && selectedTrack) {
                setSubtitlesTrack(null);
                setExtraSubtitlesTrack(null);
            }
            appliedTrack.current = null;
            return;
        }
        var selectedRank = selectedTrack && selectedSource ?
            findCandidateRank(candidates, selectedSource, selectedTrack)
            :
                -1;
        var trackToApply = selectedRank === bestCandidate.rank && selectedTrack && selectedSource ?
            { source: selectedSource, track: selectedTrack }
            :
                bestCandidate;
        // Reapply once per stream even if the controller already reports the same track.
        if (((_b = appliedTrack.current) === null || _b === void 0 ? void 0 : _b.id) === trackToApply.track.id &&
            appliedTrack.current.source === trackToApply.source) {
            return;
        }
        trackToApply.source === 'embedded' ?
            setSubtitlesTrack(trackToApply.track.id)
            :
                setExtraSubtitlesTrack(trackToApply.track.id);
        var delay = (_c = player.streamState) === null || _c === void 0 ? void 0 : _c.subtitleDelay;
        // Selecting an external track resets its delay in stremio-video.
        if (trackToApply.source === 'external' && typeof delay === 'number') {
            setSubtitlesDelay(delay);
        }
        appliedTrack.current = {
            id: trackToApply.track.id,
            source: trackToApply.source,
        };
    }, [
        player.subtitlePreference,
        player.streamState,
        settings.subtitlesLanguage,
        setSubtitlesTrack,
        setExtraSubtitlesTrack,
        setSubtitlesDelay,
        video.state.extraSubtitlesTracks,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
        video.state.stream,
        video.state.subtitlesTracks,
    ]);
    (0, react_1.useEffect)(function () {
        var _a, _b, _c;
        if (video.state.stream === null) {
            return;
        }
        var delay = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleDelay;
        if (typeof delay === 'number') {
            setSubtitlesDelay(delay);
        }
        var size = (_b = player.streamState) === null || _b === void 0 ? void 0 : _b.subtitleSize;
        if (typeof size === 'number') {
            setSubtitlesSize(size);
        }
        var offset = (_c = player.streamState) === null || _c === void 0 ? void 0 : _c.subtitleOffset;
        if (typeof offset === 'number') {
            setSubtitlesOffset(offset);
        }
    }, [player.streamState, video.state.stream, setSubtitlesDelay, setSubtitlesSize, setSubtitlesOffset]);
    (0, react_1.useEffect)(function () {
        if (!hasTracks) {
            closeSubtitlesMenu();
        }
    }, [closeSubtitlesMenu, hasTracks]);
    (0, react_1.useEffect)(function () {
        var onSubtitlesTrackLoaded = function () {
            toast.show({
                type: 'success',
                title: t('PLAYER_SUBTITLES_LOADED'),
                message: t('PLAYER_SUBTITLES_LOADED_EMBEDDED'),
                timeout: 3000,
            });
        };
        var onExtraSubtitlesTrackLoaded = function (track) {
            toast.show({
                type: 'success',
                title: t('PLAYER_SUBTITLES_LOADED'),
                message: track.exclusive ?
                    t('PLAYER_SUBTITLES_LOADED_EXCLUSIVE')
                    :
                        track.local ?
                            t('PLAYER_SUBTITLES_LOADED_LOCAL')
                            :
                                t('PLAYER_SUBTITLES_LOADED_ORIGIN', { origin: track.origin }),
                timeout: 3000,
            });
        };
        var onExtraSubtitlesTrackAdded = function (track) {
            if (track.local) {
                videoRef.current.setExtraSubtitlesTrack(track.id);
            }
        };
        video.events.on('subtitlesTrackLoaded', onSubtitlesTrackLoaded);
        video.events.on('extraSubtitlesTrackLoaded', onExtraSubtitlesTrackLoaded);
        video.events.on('extraSubtitlesTrackAdded', onExtraSubtitlesTrackAdded);
        video.events.on('implementationChanged', applySubtitleStyle);
        return function () {
            video.events.off('subtitlesTrackLoaded', onSubtitlesTrackLoaded);
            video.events.off('extraSubtitlesTrackLoaded', onExtraSubtitlesTrackLoaded);
            video.events.off('extraSubtitlesTrackAdded', onExtraSubtitlesTrackAdded);
            video.events.off('implementationChanged', applySubtitleStyle);
        };
    }, [applySubtitleStyle, t, toast, video.events]);
    (0, common_1.useShortcut)('subtitlesDelay', startSubtitleDelayHold, !menusOpen);
    (0, common_1.useShortcut)('subtitlesSize', (0, react_1.useCallback)(function (combo) {
        combo === 1 ? updateSize(1) : updateSize(-1);
    }, [updateSize]), !menusOpen);
    (0, common_1.useShortcut)('toggleSubtitles', (0, react_1.useCallback)(function () {
        var _a, _b, _c, _d, _e, _f;
        var subtitlesEnabled = video.state.selectedSubtitlesTrackId !== null ||
            video.state.selectedExtraSubtitlesTrackId !== null;
        if (subtitlesEnabled) {
            if (video.state.selectedSubtitlesTrackId) {
                var track = findTrackById(video.state.subtitlesTracks, video.state.selectedSubtitlesTrackId);
                var language_1 = normalizeLanguage(track === null || track === void 0 ? void 0 : track.lang);
                lastSelectedTrack.current = __assign({ id: video.state.selectedSubtitlesTrackId, embedded: true }, (language_1 ? { language: language_1 } : {}));
            }
            else if (video.state.selectedExtraSubtitlesTrackId) {
                var track = findTrackById(video.state.extraSubtitlesTracks, video.state.selectedExtraSubtitlesTrackId);
                var language_2 = normalizeLanguage(track === null || track === void 0 ? void 0 : track.lang);
                lastSelectedTrack.current = __assign({ id: video.state.selectedExtraSubtitlesTrackId, embedded: false }, (language_2 ? { language: language_2 } : {}));
            }
            disableSubtitles();
            return;
        }
        var savedTrack = (_b = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleTrack) !== null && _b !== void 0 ? _b : lastSelectedTrack.current;
        var source = (_d = (_c = player.subtitlePreference) === null || _c === void 0 ? void 0 : _c.source) !== null && _d !== void 0 ? _d : (savedTrack ? (savedTrack.embedded ? 'embedded' : 'external') : undefined);
        var language = (_f = (_e = player.subtitlePreference) === null || _e === void 0 ? void 0 : _e.language) !== null && _f !== void 0 ? _f : normalizeLanguage(savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.language);
        trackSelectionLocked.current = false;
        appliedTrack.current = null;
        subtitlePreferenceChanged(__assign(__assign({ enabled: true }, (source ? { source: source } : {})), (language ? { language: language } : {})));
    }, [
        disableSubtitles,
        player.subtitlePreference,
        player.streamState,
        subtitlePreferenceChanged,
        video.state.extraSubtitlesTracks,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
        video.state.subtitlesTracks,
    ]), !menusOpen);
    (0, common_1.useShortcut)('subtitlesMenu', (0, react_1.useCallback)(function () {
        closeMenus();
        if (hasTracks) {
            toggleSubtitlesMenu();
        }
    }, [closeMenus, hasTracks, toggleSubtitlesMenu]));
    var menuProps = (0, react_1.useMemo)(function () { return ({
        subtitlesLanguage: settings.subtitlesLanguage,
        interfaceLanguage: settings.interfaceLanguage,
        subtitlesTracks: video.state.subtitlesTracks,
        selectedSubtitlesTrackId: video.state.selectedSubtitlesTrackId,
        subtitlesOffset: video.state.subtitlesOffset,
        subtitlesSize: video.state.subtitlesSize,
        extraSubtitlesTracks: video.state.extraSubtitlesTracks,
        selectedExtraSubtitlesTrackId: video.state.selectedExtraSubtitlesTrackId,
        extraSubtitlesOffset: video.state.extraSubtitlesOffset,
        extraSubtitlesDelay: video.state.extraSubtitlesDelay,
        extraSubtitlesSize: video.state.extraSubtitlesSize,
        assSubtitlesStylingActive: video.state.assSubtitlesStylingActive,
        onSubtitlesTrackSelected: selectEmbeddedTrack,
        onExtraSubtitlesTrackSelected: selectExtraTrack,
        onSubtitlesOffsetChanged: changeOffset,
        onSubtitlesSizeChanged: changeSize,
        onExtraSubtitlesOffsetChanged: changeOffset,
        onExtraSubtitlesDelayChanged: changeDelay,
        onExtraSubtitlesSizeChanged: changeSize,
    }); }, [
        changeDelay,
        changeOffset,
        changeSize,
        selectEmbeddedTrack,
        selectExtraTrack,
        settings.interfaceLanguage,
        settings.subtitlesLanguage,
        video.state.extraSubtitlesDelay,
        video.state.extraSubtitlesOffset,
        video.state.extraSubtitlesSize,
        video.state.extraSubtitlesTracks,
        video.state.assSubtitlesStylingActive,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
        video.state.subtitlesOffset,
        video.state.subtitlesSize,
        video.state.subtitlesTracks,
    ]);
    return {
        streamSubtitles: streamSubtitles,
        allSubtitleTracks: allTracks,
        extraSubtitleTracks: video.state.extraSubtitlesTracks,
        selectedExtraSubtitleTrackId: video.state.selectedExtraSubtitlesTrackId,
        subtitlesMenuProps: menuProps,
    };
};
exports.default = useSubtitles;
